/**
 * ReconcileGate — concurrent-collapsing + abort-bounded reconcile primitive.
 *
 * Replaces the per-view ad-hoc combination of:
 *   - module-level `reconcileInflight` (or `chatReconcileInflight`) boolean
 *   - `new AbortController()` + `setTimeout(() => controller.abort(), 15_000)`
 *   - `try { ... } catch { return errorValue } finally { clearTimeout; lock=false }`
 *
 * Each view's reconcile fn reduces to:
 *   gate.run(inflightValue, errorValue, async (signal) => { ...body using signal... })
 *
 * Behaviorally identical to the pre-extraction shape; the lock + abort
 * scaffolding is now structural, not by-convention. A future refactor that
 * loses the `clearTimeout` or the lock release in finally is impossible —
 * those live inside the primitive and are pinned by source-level contract.
 *
 * Pattern reference: WsTopic primitive (commits 12249c2, 83b1f62 ancestors)
 * collapsed 5 hand-rolled WS subscriber registries to one generic class with
 * 5 typed-key topics. ReconcileGate does the same for reconcile scaffolding.
 */

/**
 * Reconcile fetch ceiling. iPhone Safari TLS warm-up tops out at ~10s
 * (measured 2026-05-03 in `bug3_v32_deep_probe_1777786713.json`); 15s gives
 * 50% headroom so the FIRST attempt completes in the warm-up case (no retry
 * needed). History: 30s (v0.79.29 — held lock past poll cycle) → 7s (v0.79.32
 * — killed before TLS warm-up) → 15s (v0.79.33 — current).
 *
 * Workbox `/player-state` route is `NetworkOnly` with no timeout knob; the
 * wedge surface is a PAGE-LEVEL Promise hang BEFORE the request enters the
 * SW pipeline, so only this page-side AbortController can break it. If you
 * change this value, also update behavioral assertions in
 * `pwa/tests/reconcile-timeout-contract.test.ts`.
 */
export const RECONCILE_FETCH_TIMEOUT_MS = 15_000;

export class ReconcileGate {
  private inflight = false;
  // Per-run generation token. Bumped on every `run()` acquire AND on every
  // `release()` call. The body's `finally` only clears `inflight` when its
  // captured `myGeneration` still matches the current `generation` — this
  // closes the [P2] race surfaced by `/codex review` 2026-05-07: when a view
  // destroy → release() lands while body1 is still awaiting AND a new mount's
  // run() acquires the lock before body1 resolves, body1's stale `finally`
  // would otherwise stomp on body2's `inflight = true` and let a third call
  // re-enter the gate concurrently with body2. Pre-fix this race existed
  // identically in the per-view `reconcileInflight = false` boolean pattern;
  // codifying the gate is the cheap place to actually close it.
  private generation = 0;

  /**
   * Acquire lock + run body with timeout-bounded AbortSignal.
   *
   * Returns inflightValue if a prior run is still pending (concurrent calls
   * are collapsed — iOS Safari fires visibilitychange + pageshow within
   * milliseconds on resume, lock makes the second a no-op).
   *
   * Returns errorValue if body throws (incl. AbortError on timeout, network
   * error, parse error). Lock + timer are released in finally regardless of
   * body outcome — the lock-release only fires when the finalizing run still
   * owns the current generation (see `generation` field comment for the race).
   */
  async run<R>(
    inflightValue: R,
    errorValue: R,
    body: (signal: AbortSignal) => Promise<R>,
  ): Promise<R> {
    if (this.inflight) return inflightValue;
    this.inflight = true;
    const myGeneration = ++this.generation;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), RECONCILE_FETCH_TIMEOUT_MS);
    try {
      return await body(controller.signal);
    } catch {
      return errorValue;
    } finally {
      clearTimeout(timeoutId);
      // Only clear the lock if no `release()` or newer `run()` has bumped the
      // generation past us. A stale finally MUST NOT stomp on a newer run's
      // `inflight = true` — that would let a third call enter the gate while
      // body2 is still awaiting (the [P2] race; see field comment above).
      if (this.generation === myGeneration) {
        this.inflight = false;
      }
    }
  }

  /**
   * Run body with timeout but WITHOUT acquiring the inflight lock.
   *
   * For cold-mount entry points that intentionally allow concurrent runs.
   * Currently the only consumer is location-view's `reconcileFromServer`
   * (called directly from `renderLocationView`); visibility/pageshow handlers
   * wrap the same body via `run()` to serialize.
   *
   * Returns errorValue on body throw. Timer cleared in finally regardless.
   */
  async runWithoutLock<R>(
    errorValue: R,
    body: (signal: AbortSignal) => Promise<R>,
  ): Promise<R> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), RECONCILE_FETCH_TIMEOUT_MS);
    try {
      return await body(controller.signal);
    } catch {
      return errorValue;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Reset the inflight lock. Call from view destroy to free a stale stuck-inflight
   * state — a fetch in flight at view tear-down would clear the flag in its own
   * finally, but that finally runs AFTER the next mount's first
   * visibility/pageshow flip, causing the new mount's reconcile to bail at the
   * lock guard. Matches the destroy-resets pattern in pre-extraction lobby-view,
   * chat-view, location-view, spectator-view destroy fns. Does not abort the
   * in-flight body; the body's own AbortController will still fire on its 15s
   * timeout (or resolve naturally if the response lands first). The lock-release
   * just frees the gate for the next mount.
   *
   * Bumps `generation` so any pending in-flight run's `finally` becomes stale
   * and skips the unconditional `inflight = false` that would otherwise stomp
   * on a newer mount's lock (the [P2] race surfaced by `/codex review`).
   * Idempotent (no-op effects on flag are safe; generation just keeps bumping).
   */
  release(): void {
    this.inflight = false;
    this.generation++;
  }

  /**
   * Inspect lock state. Used by chat-view's WS REACTION_CHANGED handler to
   * decide whether to flag a message id for the in-flight reconcile (so it
   * prefers local reactions over the older server snapshot). Read-only —
   * don't write through this getter.
   */
  get isInflight(): boolean {
    return this.inflight;
  }
}
